import React, { useMemo } from 'react';
import useAnimatedPositions from '../useAnimatedPositions';

// Groups cars within `thresholdPx` of each other (chained/transitive — a queue of
// nose-to-tail cars merges into one cluster even if the first and last are far
// apart) so their acronym labels can be stacked into one readable block instead
// of overlapping, matching how broadcast/timing-app track maps handle close
// racing. Dots always stay at their true positions; only the label groups.
function clusterPositions(cars, thresholdPx = 28) {
  const n = cars.length;
  const visited = new Array(n).fill(false);
  const clusters = [];

  for (let i = 0; i < n; i++) {
    if (visited[i]) continue;
    visited[i] = true;
    const stack = [i];
    const members = [];

    while (stack.length) {
      const idx = stack.pop();
      members.push(cars[idx]);
      for (let j = 0; j < n; j++) {
        if (visited[j]) continue;
        const dist = Math.hypot(cars[idx].x - cars[j].x, cars[idx].y - cars[j].y);
        if (dist < thresholdPx) {
          visited[j] = true;
          stack.push(j);
        }
      }
    }

    const centroidX = members.reduce((s, m) => s + m.x, 0) / members.length;
    const centroidY = members.reduce((s, m) => s + m.y, 0) / members.length;
    clusters.push({ members, centroidX, centroidY });
  }

  return clusters;
}

const DOT_RADIUS = 6;
const LABEL_OFFSET_X = 14;
const LABEL_OFFSET_Y = -14;
const ROW_HEIGHT = 13;
const CLUSTER_DIST = 28; // px in the 1000x560 viewBox space — needs tuning against the real screen

export default function TrackMap({ outline, carPositions, teamColours, topOrder }) {
  const animatedPositions = useAnimatedPositions(carPositions);
  const clusters = useMemo(() => {
    if (!animatedPositions) return [];
    const orderIndex = new Map((topOrder || []).map((acr, i) => [acr, i]));
    const cars = Object.entries(animatedPositions)
      .filter(([, pos]) => pos && Number.isFinite(pos.x) && Number.isFinite(pos.y))
      .map(([acronym, pos]) => ({ acronym, x: pos.x, y: pos.y }));
    const grouped = clusterPositions(cars, CLUSTER_DIST);
    for (const c of grouped) {
      c.members.sort((a, b) => (orderIndex.get(a.acronym) ?? 99) - (orderIndex.get(b.acronym) ?? 99));
    }
    return grouped;
  }, [animatedPositions, topOrder]);

  if (!outline) return null;

  return (
    <svg viewBox={outline.viewBox} style={{ width: '100%', height: '100%', display: 'block' }}>
      {/* Road */}
      <path
        d={outline.path}
        fill="none"
        stroke="#2c2c2c"
        strokeWidth={14}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* Dashed centerline (lane marking) */}
      <path
        d={outline.path}
        fill="none"
        stroke="#4a4a4a"
        strokeWidth={1}
        strokeDasharray="4,6"
      />

      {/* Dots rendered as one flat list keyed purely by acronym — NOT nested inside
          per-cluster <g> wrappers keyed by cluster index. Cluster membership
          reshuffles constantly as cars bunch up/separate on track; if a dot's
          parent boundary changes identity between renders, React unmounts and
          remounts it instead of transitioning, which kills the CSS glide and
          looks like a snap/jump. Keying flat by acronym keeps each dot's DOM
          node — and therefore its transition — stable across every re-cluster. */}
      {clusters.flatMap(cluster => cluster.members).map(m => {
        const dotColour = teamColours?.[m.acronym] ? `#${teamColours[m.acronym]}` : '#888';
        return (
          <circle
            key={m.acronym}
            cx={m.x}
            cy={m.y}
            r={DOT_RADIUS}
            fill={dotColour}
            stroke="#0a0a0a"
            strokeWidth={1.5}
          />
        );
      })}

      {/* Label groups — leader line + stacked acronym block per cluster */}
      {clusters.map(cluster => {
        const tc = teamColours?.[cluster.members[0].acronym];
        const anchorColour = tc ? `#${tc}` : '#f9b000';
        const labelX = cluster.centroidX + LABEL_OFFSET_X;
        const labelY = cluster.centroidY + LABEL_OFFSET_Y;
        const blockH = cluster.members.length * ROW_HEIGHT;
        const clusterKey = cluster.members.map(m => m.acronym).join('-');

        return (
          <g key={clusterKey}>
            {/* Leader line from cluster centroid to label block */}
            <line
              x1={cluster.centroidX}
              y1={cluster.centroidY}
              x2={labelX - 2}
              y2={labelY + blockH / 2}
              stroke={anchorColour}
              strokeWidth={0.75}
              opacity={0.5}
            />

            {/* Stacked label block */}
            <rect
              x={labelX - 3}
              y={labelY - ROW_HEIGHT + 2}
              width={38}
              height={blockH}
              rx={2}
              fill="#0a0a0acc"
              stroke="#222"
              strokeWidth={0.5}
            />
            {cluster.members.map((m, i) => {
              const dotColour = teamColours?.[m.acronym] ? `#${teamColours[m.acronym]}` : '#888';
              const rowY = labelY - ROW_HEIGHT + 2 + i * ROW_HEIGHT;
              return (
                <g key={m.acronym}>
                  <rect x={labelX} y={rowY + 2} width={4} height={9} fill={dotColour} />
                  <text
                    x={labelX + 8}
                    y={rowY + 9.5}
                    fontFamily="var(--font-mono)"
                    fontSize={9.5}
                    fontWeight={700}
                    fill="#e8e8e8"
                    letterSpacing="0.02em"
                  >
                    {m.acronym}
                  </text>
                </g>
              );
            })}
          </g>
        );
      })}
    </svg>
  );
}
